import React from "react";
import {
  Stack,
  Typography,
  Card,
  Tooltip,
  Icon,
  IconButton,
  Alert,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
} from "investira.react.components";
import { PrecisionNumberDisplay } from "../../../../components/molecules";
import { formats, validators } from "investira.sdk";

const Analise = (props) => {
  const { resumo } = props.data.response;
  const { indicador_id } = props.data.request;

  function calcDecimal(pValue, pMinSigDigitos = 4) {
    const xMatch = pValue.toString().match(/e-(\d+)/);
    const xExpoente = xMatch ? parseInt(xMatch[1], 10) : 0;

    // Calcula as casas decimais necessárias (expoente + dígitos significativos - 1)
    const xDecimal = xExpoente + pMinSigDigitos;
    return xDecimal;
  }

  return (
    <>
      <Stack direction="column" sx={{ gap: 1 }} component="section">
        <Stack
          sx={{
            flex: 1,
          }}
        >
          <Card sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between">
              <Typography
                variant={"body1"}
                color="textPrimary"
                sx={{ fontWeight: 500 }}
              >
                VaR
              </Typography>
              <Tooltip
                title={
                  <React.Fragment>
                    <Typography
                      color="inherit"
                      variant="caption"
                      component="p"
                      gutterBottom
                    >
                      <b>VaR (Value at Risk)</b>
                    </Typography>
                    <Typography
                      color="inherit"
                      variant="caption"
                      component="p"
                      gutterBottom
                    >
                      Estima a perda máxima esperada em um período, com um nível
                      de confiança.
                    </Typography>
                    <Typography
                      color="inherit"
                      variant="caption"
                      component="p"
                      sx={{ fontWeight: 500 }}
                    >
                      Valores de Referência:
                    </Typography>
                    <Typography
                      color="inherit"
                      variant="caption"
                      component="p"
                      gutterBottom
                    >
                      Mínimo: VaR igual a 0 seria teórico (nenhum risco), mas é
                      irrealista.
                    </Typography>
                    <Typography
                      color="inherit"
                      variant="caption"
                      component="p"
                      sx={{ fontWeight: 500 }}
                    >
                      Ideal:
                    </Typography>
                    <Typography color="inherit" variant="caption" component="p">
                      Depende do nível de confiança (ex.: 95% ou 99%) e do
                      horizonte temporal (ex.: 1 dia, 1 mês).
                    </Typography>
                    <Typography color="inherit" variant="caption" component="p">
                      Exemplo: VaR diário de 1% a 5% do valor da carteira é
                      comum para portfólios moderados em mercados estáveis.
                    </Typography>

                    <Typography
                      color="inherit"
                      variant="caption"
                      component="p"
                      gutterBottom
                    >
                      {`Máximo: Depende do tamanho da carteira e da volatilidade.
                      VaR elevado (ex.: >10% diário) indica alto risco.`}
                    </Typography>
                    <Typography color="inherit" variant="caption" component="p">
                      Um VaR de 5% a 95% de confiança significa que, em 95% dos
                      dias, a perda não excederá 5% do valor da carteira.
                      Comparar com benchmarks ou carteiras semelhantes.
                    </Typography>
                  </React.Fragment>
                }
              >
                <span>
                  <Icon size={16} color={"primary"} iconName="information" />
                </span>
              </Tooltip>
            </Stack>
            <Stack>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="center"></TableCell>
                    <TableCell colSpan={2} align="center">
                      Anual
                    </TableCell>
                    <TableCell colSpan={2} align="center">
                      Mensal
                    </TableCell>
                    <TableCell colSpan={2} align="center">
                      Diário
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[
                    {
                      key: "historico",
                      label: "Histórico",
                    },
                    {
                      key: "parametrico",
                      label: "Paramétrico",
                    },
                    {
                      key: "parametrico_ewma",
                      label: "Paramétrico EWMA",
                    },
                  ].map((varType, idx) => {
                    const confiancas = Number(
                      props.data.request.confiancas
                    ).toFixed(2);

                    const varData =
                      resumo.var &&
                      resumo.var[varType.key] &&
                      resumo.var[varType.key][confiancas]
                        ? resumo.var[varType.key][confiancas]
                        : null;

                    return (
                      <React.Fragment key={varType.key}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, width: "100%" }}>
                            {varType.label}
                          </TableCell>

                          <TableCell align="right" sx={{ minWidth: "120px" }}>
                            <PrecisionNumberDisplay
                              value={varData.financeiro_anual}
                              threshold={0.01}
                              showCurrency={true}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ minWidth: "120px" }}>
                            <PrecisionNumberDisplay
                              value={varData.percentual_anual * 100}
                              threshold={0.0001}
                              sufix={"%"}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ minWidth: "120px" }}>
                            <PrecisionNumberDisplay
                              value={varData.financeiro_mensal}
                              threshold={0.01}
                              showCurrency={true}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ minWidth: "120px" }}>
                            <PrecisionNumberDisplay
                              value={varData.percentual_mensal * 100}
                              threshold={0.0001}
                              sufix={"%"}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ minWidth: "120px" }}>
                            <PrecisionNumberDisplay
                              value={varData.financeiro_diario}
                              threshold={0.01}
                              showCurrency={true}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ minWidth: "120px" }}>
                            <PrecisionNumberDisplay
                              value={varData.percentual_diario * 100}
                              threshold={0.0001}
                              sufix={"%"}
                            />
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </Stack>
          </Card>
        </Stack>
        <Stack
          sx={{
            flex: 1,
          }}
        >
          <Card sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between">
              <Typography
                variant={"body1"}
                color="textPrimary"
                sx={{ fontWeight: 500 }}
              >
                Volatilidade
              </Typography>
              <Tooltip
                title={
                  <React.Fragment>
                    <Typography
                      color="inherit"
                      variant="caption"
                      component="p"
                      gutterBottom
                    >
                      <b>Volatilidade</b>
                    </Typography>
                    <Typography
                      color="inherit"
                      variant="caption"
                      component="p"
                      gutterBottom
                    >
                      Mede a magnitude das oscilações de preço (desvio padrão
                      dos retornos).
                    </Typography>
                    <Typography
                      color="inherit"
                      variant="caption"
                      component="p"
                      sx={{ fontWeight: 500 }}
                    >
                      Valores de Referência:
                    </Typography>
                    <Typography
                      color="inherit"
                      variant="caption"
                      component="p"
                      gutterBottom
                    >
                      Mínimo: Volatilidade próxima de 0 é típica de ativos
                      estáveis (ex.: títulos públicos).
                    </Typography>
                    <Typography
                      color="inherit"
                      variant="caption"
                      component="p"
                      sx={{ fontWeight: 500 }}
                    >
                      Ideal:
                    </Typography>
                    <Typography color="inherit" variant="caption" component="p">
                      {`Baixa: <10% anual (ex.: renda fixa ou ações defensivas).`}
                    </Typography>
                    <Typography color="inherit" variant="caption" component="p">
                      {`Moderada: 10-20% anual (ex.: índices de mercado como
                      Ibovespa ou S&P 500).`}
                    </Typography>
                    <Typography color="inherit" variant="caption" component="p">
                      {`Alta: >20% anual (ex.: ações de tecnologia ou mercados emergentes).`}
                    </Typography>

                    <Typography
                      color="inherit"
                      variant="caption"
                      component="p"
                      gutterBottom
                    >
                      {`Máximo: Sem limite, mas volatilidades acima de 50% são extremas (ex.: criptomoedas).`}
                    </Typography>
                    <Typography color="inherit" variant="caption" component="p">
                      Maior volatilidade implica maior risco. Comparar com a
                      volatilidade do mercado ou setor para contextualizar.
                    </Typography>
                  </React.Fragment>
                }
              >
                <span>
                  <Icon size={16} color={"primary"} iconName="information" />
                </span>
              </Tooltip>
            </Stack>
            <Stack>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="right"></TableCell>
                    <TableCell align="center" colSpan={2}>
                      Anual
                    </TableCell>
                    <TableCell align="center" colSpan={2}>
                      Mensal
                    </TableCell>
                    <TableCell align="center" colSpan={2}>
                      Diário
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ width: "100%" }}>Padrão</TableCell>
                    <TableCell sx={{ minWidth: "120px" }}>---</TableCell>
                    <TableCell align="right" sx={{ minWidth: "120px" }}>
                      <PrecisionNumberDisplay
                        value={resumo.vol.anual * 100}
                        threshold={0.0001}
                        sufix={"%"}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: "120px" }}>---</TableCell>
                    <TableCell align="right" sx={{ minWidth: "120px" }}>
                      <PrecisionNumberDisplay
                        value={resumo.vol.mensal * 100}
                        threshold={0.0001}
                        sufix={"%"}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: "120px" }}>---</TableCell>
                    <TableCell align="right" sx={{ minWidth: "120px" }}>
                      <PrecisionNumberDisplay
                        value={resumo.vol.diario * 100}
                        threshold={0.0001}
                        sufix={"%"}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ width: "100%" }}>EWMA</TableCell>
                    <TableCell sx={{ minWidth: "120px" }}>---</TableCell>
                    <TableCell align="right" sx={{ minWidth: "120px" }}>
                      <PrecisionNumberDisplay
                        value={resumo.vol.anual_ewma * 100}
                        threshold={0.0001}
                        sufix={"%"}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: "120px" }}>---</TableCell>
                    <TableCell align="right" sx={{ minWidth: "120px" }}>
                      <PrecisionNumberDisplay
                        value={resumo.vol.mensal_ewma * 100}
                        threshold={0.0001}
                        sufix={"%"}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: "120px" }}>---</TableCell>
                    <TableCell align="right" sx={{ minWidth: "120px" }}>
                      <PrecisionNumberDisplay
                        value={resumo.vol.diario_ewma * 100}
                        threshold={0.0001}
                        sufix={"%"}
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Stack>
          </Card>
        </Stack>
        {indicador_id && (
          <Stack
            sx={{
              flex: 1,
            }}
          >
            <Card sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between">
                <Typography
                  variant={"body1"}
                  color="textPrimary"
                  sx={{ fontWeight: 500 }}
                >
                  BVaR
                </Typography>
                <Tooltip
                  title={
                    <React.Fragment>
                      <Typography
                        color="inherit"
                        variant="caption"
                        component="p"
                        gutterBottom
                      >
                        <b>Benchmarking VaR</b>
                      </Typography>
                      <Typography
                        color="inherit"
                        variant="caption"
                        component="p"
                        gutterBottom
                      >
                        Estima a perda máxima relativa a um benchmark, em um
                        período e nível de confiança.
                      </Typography>
                      <Typography
                        color="inherit"
                        variant="caption"
                        component="p"
                        sx={{ fontWeight: 500 }}
                      >
                        Valores de Referência:
                      </Typography>
                      <Typography
                        color="inherit"
                        variant="caption"
                        component="p"
                        gutterBottom
                      >
                        Mínimo: BVaR igual a 0 indicaria desempenho idêntico ao
                        benchmark, raro na prática.
                      </Typography>
                      <Typography
                        color="inherit"
                        variant="caption"
                        component="p"
                        sx={{ fontWeight: 500 }}
                      >
                        {`Ideal: Depende do benchmark e da estratégia. Um BVaR baixo (ex.: <2%) sugere que a carteira acompanha o benchmark de perto.`}
                      </Typography>
                      <Typography
                        color="inherit"
                        variant="caption"
                        component="p"
                        gutterBottom
                      >
                        {`Máximo: BVaR elevado (ex.: >5%) indica risco significativo de subdesempenho em relação ao benchmark.`}
                      </Typography>
                      <Typography
                        color="inherit"
                        variant="caption"
                        component="p"
                      >
                        Útil para gestores ativos. Um BVaR alto pode indicar
                        desvios intencionais do benchmark (estratégia ativa) ou
                        má gestão.
                      </Typography>
                    </React.Fragment>
                  }
                >
                  <span>
                    <Icon size={16} color={"primary"} iconName="information" />
                  </span>
                </Tooltip>
              </Stack>
              {!validators.isEmpty(resumo.bvar) && (
                <Stack>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell align="center"></TableCell>
                        <TableCell colSpan={2} align="center">
                          Anual
                        </TableCell>
                        <TableCell colSpan={2} align="center">
                          Mensal
                        </TableCell>
                        <TableCell colSpan={2} align="center">
                          Diário
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[
                        {
                          key: "historico",
                          label: "Histórico",
                        },
                        {
                          key: "parametrico",
                          label: "Paramétrico",
                        },
                        {
                          key: "parametrico_ewma",
                          label: "EWMA",
                        },
                      ].map((bvarType, idx) => {
                        const confiancas = Number(
                          props.data.request.confiancas
                        ).toFixed(2);

                        const bvarData =
                          resumo.bvar &&
                          resumo.bvar[bvarType.key] &&
                          resumo.bvar[bvarType.key][confiancas]
                            ? resumo.bvar[bvarType.key][confiancas]
                            : null;

                        return (
                          <React.Fragment key={bvarType.key}>
                            <TableRow>
                              <TableCell
                                sx={{ fontWeight: 600, width: "100%" }}
                              >
                                {bvarType.label}
                              </TableCell>

                              <TableCell
                                align="right"
                                sx={{ minWidth: "120px" }}
                              >
                                <PrecisionNumberDisplay
                                  value={bvarData.financeiro_anual}
                                  threshold={0.01}
                                  showCurrency={true}
                                />
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ minWidth: "120px" }}
                              >
                                <PrecisionNumberDisplay
                                  value={bvarData.percentual_anual * 100}
                                  threshold={0.0001}
                                  sufix={"%"}
                                />
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ minWidth: "120px" }}
                              >
                                <PrecisionNumberDisplay
                                  value={bvarData.financeiro_mensal}
                                  threshold={0.01}
                                  showCurrency={true}
                                />
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ minWidth: "120px" }}
                              >
                                <PrecisionNumberDisplay
                                  value={bvarData.percentual_mensal * 100}
                                  threshold={0.0001}
                                  sufix={"%"}
                                />
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ minWidth: "120px" }}
                              >
                                <PrecisionNumberDisplay
                                  value={bvarData.financeiro_diario}
                                  threshold={0.01}
                                  showCurrency={true}
                                />
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ minWidth: "120px" }}
                              >
                                <PrecisionNumberDisplay
                                  value={bvarData.percentual_diario * 100}
                                  threshold={0.0001}
                                  sufix={"%"}
                                />
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Stack>
              )}
            </Card>
          </Stack>
        )}
      </Stack>
    </>
  );
};

export default Analise;
